import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FolderIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { projectsAPI, tasksAPI, teamsAPI, usersAPI, analyticsAPI, authAPI } from '../../services/api';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ProjectModal from '../../components/admin/ProjectModal';
import TaskModal from '../../components/admin/TaskModal';
import TaskDetailsModal from '../../components/admin/TaskDetailsModal';
import RecurringTaskModal from '../../components/admin/RecurringTaskModal';
import RecurringTaskDetailsModal from '../../components/admin/RecurringTaskDetailsModal';
import toast from 'react-hot-toast';

interface Project {
  id: string;
  name: string;
  description?: string;
  status: string;
  priority: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  budget?: number;
  color: string;
  teamId: string;
  creatorId: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  creator: any;
  team?: any;
  tasks: any[];
  _count: { tasks: number };
}

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  dueDate?: string;
  creator: any;
  assignees: any[];
  team?: any;
  project?: any;
}

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);
  const [standaloneTasks, setStandaloneTasks] = useState<Task[]>([]);
  const [standaloneRecurringTasks, setStandaloneRecurringTasks] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [viewMode, setViewMode] = useState<'projects' | 'archived' | 'tasks' | 'recurring-tasks' | 'analytics'>('projects');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [teams, setTeams] = useState<any[]>([]);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isTaskDetailsModalOpen, setIsTaskDetailsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [isRecurringTaskModalOpen, setIsRecurringTaskModalOpen] = useState(false);
  const [editingRecurringTask, setEditingRecurringTask] = useState<any | null>(null);
  const [isRecurringTaskDetailsModalOpen, setIsRecurringTaskDetailsModalOpen] = useState(false);
  const [selectedRecurringTask, setSelectedRecurringTask] = useState<any | null>(null);

  // Pagination and filtering state for standalone tasks
  const [tasksCurrentPage, setTasksCurrentPage] = useState(1);
  const [tasksPerPage] = useState(10);
  const [tasksUserFilter, setTasksUserFilter] = useState('');
  const [tasksPaginationData, setTasksPaginationData] = useState<any>(null);

  // Pagination and filtering state for recurring tasks
  const [recurringTasksCurrentPage, setRecurringTasksCurrentPage] = useState(1);
  const [recurringTasksPerPage] = useState(10);
  const [recurringTasksUserFilter, setRecurringTasksUserFilter] = useState('');
  const [recurringTasksPaginationData, setRecurringTasksPaginationData] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (viewMode === 'analytics' && !analytics) {
      loadAnalytics();
    }
  }, [viewMode, analytics]);

  // Reload standalone tasks when pagination or filter changes
  useEffect(() => {
    if (users.length > 0) { // Only load after users are loaded
      loadStandaloneTasks();
    }
  }, [tasksCurrentPage, tasksUserFilter]);

  // Reload recurring tasks when pagination or filter changes
  useEffect(() => {
    if (users.length > 0) { // Only load after users are loaded
      loadStandaloneRecurringTasks();
    }
  }, [recurringTasksCurrentPage, recurringTasksUserFilter]);

  const loadStandaloneTasks = async (page: number = tasksCurrentPage, userFilter: string = tasksUserFilter) => {
    try {
      const filters: any = {
        includeProjectTasks: false,
        page,
        limit: tasksPerPage
      };

      if (userFilter) {
        filters.assigneeId = userFilter;
      }

      const tasksData = await tasksAPI.getAll(filters);

      if (tasksData && typeof tasksData === 'object' && 'data' in tasksData) {
        setStandaloneTasks(tasksData.data);
        setTasksPaginationData(tasksData);
      } else {
        setStandaloneTasks(tasksData as Task[]);
        setTasksPaginationData(null);
      }
    } catch (error) {
      console.error('Error loading standalone tasks:', error);
      toast.error('Failed to load standalone tasks');
    }
  };

  const loadStandaloneRecurringTasks = async (page: number = recurringTasksCurrentPage, userFilter: string = recurringTasksUserFilter) => {
    try {
      const filters: any = {
        page,
        limit: recurringTasksPerPage
      };

      if (userFilter) {
        filters.assigneeId = userFilter;
      }

      const recurringTasksData = await tasksAPI.getRecurringTasks(false, filters);

      if (recurringTasksData && typeof recurringTasksData === 'object' && 'data' in recurringTasksData) {
        setStandaloneRecurringTasks(recurringTasksData.data);
        setRecurringTasksPaginationData(recurringTasksData);
      } else {
        setStandaloneRecurringTasks(recurringTasksData as any[]);
        setRecurringTasksPaginationData(null);
      }
    } catch (error) {
      console.error('Error loading standalone recurring tasks:', error);
      toast.error('Failed to load standalone recurring tasks');
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [projectsData, archivedProjectsData, teamsData, usersData, userData] = await Promise.all([
        projectsAPI.getAll({ isArchived: false }),
        projectsAPI.getAll({ isArchived: true }),
        teamsAPI.getAll(),
        usersAPI.getAll(),
        authAPI.getCurrentUser(),
      ]);
      setProjects(projectsData);
      setArchivedProjects(archivedProjectsData);
      setTeams(teamsData);
      setUsers(usersData);
      setCurrentUser(userData);

      // Load tasks and recurring tasks with pagination
      await Promise.all([
        loadStandaloneTasks(),
        loadStandaloneRecurringTasks()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load projects and tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const analyticsData = await analyticsAPI.getProjectAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const handleDeleteProject = async (project: Project) => {
    setProjectToDelete(project);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      await projectsAPI.delete(projectToDelete.id);
      toast.success('Project deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete project');
    } finally {
      setIsDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const handleArchiveProject = async (project: Project) => {
    try {
      await projectsAPI.archive(project.id);
      toast.success('Project archived successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to archive project');
    }
  };

  const handleSaveProject = async (projectData: any) => {
    try {
      if (editingProject) {
        await projectsAPI.update(editingProject.id, projectData);
        toast.success('Project updated successfully');
      } else {
        await projectsAPI.create(projectData);
        toast.success('Project created successfully');
      }
      loadData();
      setIsProjectModalOpen(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('Failed to save project');
    }
  };

  const handleSaveTask = async (taskData: any) => {
    try {
      console.log('handleSaveTask called with data:', taskData);

      if (editingTask) {
        await tasksAPI.update(editingTask.id, taskData);
        toast.success('Task updated successfully');
      } else {
        if (!taskData.isRecurring) {
          // For regular tasks, ensure creatorId is included
          const taskDataWithCreator = {
            ...taskData,
            creatorId: currentUser?.id || 'cmdd8jvdu0001v1hsfzjmmox6' // Fallback
          };
          console.log('Creating regular task with data:', taskDataWithCreator);
          await tasksAPI.create(taskDataWithCreator);
          toast.success('Task created successfully');
        } else {
          // For recurring tasks, the creatorId is already included in recurringTaskData
          console.log('Creating recurring task with data:', taskData);
          await tasksAPI.create(taskData);
          toast.success('Recurring task created successfully');
        }
      }

      // Reload only the tasks data
      await loadStandaloneTasks();
      setIsTaskModalOpen(false);
      setEditingTask(null);
    } catch (error: any) {
      console.error('Error saving task:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to save task: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteTask = async (task: any) => {
    if (window.confirm(`Are you sure you want to delete "${task.title}"?`)) {
      try {
        await tasksAPI.delete(task.id);
        toast.success('Task deleted successfully');
        await loadStandaloneTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
        toast.error('Failed to delete task');
      }
    }
  };

  const handleMoveTaskToProject = async (task: any) => {
    // TODO: Implement move task to project functionality
    console.log('Move task to project:', task);
  };

  // Pagination handlers for standalone tasks
  const handleTasksPageChange = (page: number) => {
    setTasksCurrentPage(page);
  };

  const handleTasksUserFilterChange = (userId: string) => {
    setTasksUserFilter(userId);
    setTasksCurrentPage(1); // Reset to first page when filter changes
  };

  // Pagination handlers for recurring tasks
  const handleRecurringTasksPageChange = (page: number) => {
    setRecurringTasksCurrentPage(page);
  };

  const handleRecurringTasksUserFilterChange = (userId: string) => {
    setRecurringTasksUserFilter(userId);
    setRecurringTasksCurrentPage(1); // Reset to first page when filter changes
  };

  const handleEditRecurringTask = (recurringTask: any) => {
    setEditingRecurringTask(recurringTask);
    setIsRecurringTaskModalOpen(true);
  };

  const handleDeleteRecurringTask = async (recurringTask: any) => {
    if (!window.confirm(`Are you sure you want to delete the recurring task "${recurringTask.title}"?`)) {
      return;
    }

    try {
      await tasksAPI.deleteRecurringTask(recurringTask.id);
      toast.success('Recurring task deleted successfully');
      await loadStandaloneRecurringTasks(); // Reload recurring tasks data
    } catch (error) {
      console.error('Error deleting recurring task:', error);
      toast.error('Failed to delete recurring task');
    }
  };

  const handleViewRecurringTaskDetails = (recurringTask: any) => {
    setSelectedRecurringTask(recurringTask);
    setIsRecurringTaskDetailsModalOpen(true);
  };

  const handleUnarchiveProject = async (project: Project) => {
    if (window.confirm(`Are you sure you want to unarchive "${project.name}"?`)) {
      try {
        await projectsAPI.restore(project.id);
        toast.success('Project unarchived successfully');
        loadData();
      } catch (error) {
        console.error('Error unarchiving project:', error);
        toast.error('Failed to unarchive project');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PLANNING':
        return 'bg-gray-100 text-gray-800';
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800';
      case 'ON_HOLD':
        return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-500';
      case 'NORMAL':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || project.status === statusFilter;
    const matchesPriority = !priorityFilter || project.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });



  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <FolderIcon className="w-8 h-8 text-primary-600" />
              <span>Projects & Tasks</span>
            </h1>
            <p className="text-gray-600">
              Manage projects and standalone tasks ({projects.length} projects, {standaloneTasks.length} standalone tasks)
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={() => {
                  setEditingProject(null);
                  setIsProjectModalOpen(true);
                }} className="btn-primary flex items-center space-x-2">
              <PlusIcon className="w-4 h-4" />
              <span>New Project</span>
            </button>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'projects', name: 'Projects', icon: FolderIcon },
              { id: 'tasks', name: 'Standalone Tasks', icon: ClockIcon },
              { id: 'recurring-tasks', name: 'Recurring Tasks', icon: ArrowPathIcon },
              { id: 'analytics', name: 'Analytics', icon: ChartBarIcon },
              { id: 'archived', name: 'Archived', icon: ArchiveBoxIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                  viewMode === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search projects and tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Status</option>
              <option value="PLANNING">Planning</option>
              <option value="ACTIVE">Active</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="COMPLETED">Completed</option>
              <option value="TODO">To Do</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="DONE">Done</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Priority</option>
              <option value="HIGH">High</option>
              <option value="NORMAL">Normal</option>
              <option value="LOW">Low</option>
            </select>
            {/* {viewMode === 'projects' && (
              <button
                onClick={() => {
                  setEditingProject(null);
                  setIsProjectModalOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Project</span>
              </button>
            )} */}
            {viewMode === 'tasks' && (
              <button
                onClick={() => {
                  setEditingTask(null);
                  setIsTaskModalOpen(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Task</span>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {viewMode === 'projects' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-200"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: project.color }}
                      />
                      <h3 className="text-lg font-semibold text-gray-900 truncate max-w-[180px]">
                        {project.name}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${getPriorityColor(project.priority)}`} />
                    </div>
                  </div>

                  {project.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                      {project.status.replace('_', ' ')}
                    </span>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <ClockIcon className="w-4 h-4" />
                      <span>{project._count.tasks} tasks</span>
                    </div>
                  </div>

                  {project.team && (
                    <div className="flex items-center space-x-2 mb-4">
                      <UserGroupIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{project.team.name}</span>
                    </div>
                  )}

                  {project.dueDate && (
                    <div className="flex items-center space-x-2 mb-4">
                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Due {new Date(project.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/admin/projects/${project.id}`)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="View Project Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingProject(project);
                          setIsProjectModalOpen(true);
                        }}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title="Edit Project"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleArchiveProject(project)}
                        className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                      >
                        <ArchiveBoxIcon className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteProject(project)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(project.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {viewMode === 'archived' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <ArchiveBoxIcon className="w-5 h-5 text-yellow-600" />
                <h3 className="text-sm font-medium text-yellow-800">Archived Projects</h3>
              </div>
              <p className="mt-1 text-sm text-yellow-700">
                These projects have been archived. You can unarchive them to make them active again.
              </p>
            </div>

            {archivedProjects.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No archived projects</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Projects that are archived will appear here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {archivedProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="bg-gray-50 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-200 opacity-75"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full opacity-50"
                            style={{ backgroundColor: project.color }}
                          />
                          <h3 className="text-lg font-semibold text-gray-700 truncate">
                            {project.name}
                          </h3>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleUnarchiveProject(project)}
                            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                            title="Unarchive Project"
                          >
                            <ArchiveBoxIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {project.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {project.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-600`}>
                            ARCHIVED
                          </span>
                          <span className="text-gray-500">
                            {project._count.tasks} task{project._count.tasks !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <span className="text-gray-500">
                          Archived {new Date(project.updatedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {viewMode === 'tasks' && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Standalone Tasks</h3>
                <button
                  onClick={() => {
                    setEditingTask(null);
                    setIsTaskModalOpen(true);
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Task
                </button>
              </div>

              {/* User Filter */}
              <div className="mb-4">
                <select
                  value={tasksUserFilter}
                  onChange={(e) => handleTasksUserFilterChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Users</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {tasksPaginationData && (
                  <span className="ml-4 text-sm text-gray-500">
                    Showing {((tasksPaginationData.page - 1) * tasksPaginationData.limit) + 1} to {Math.min(tasksPaginationData.page * tasksPaginationData.limit, tasksPaginationData.total)} of {tasksPaginationData.total} tasks
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {standaloneTasks.map((task, index) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                      <div>
                        <h4 className="font-medium text-gray-900">{task.title}</h4>
                        {task.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 max-w-md">{task.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                        {task.status.replace('_', ' ')}
                      </span>
                      {task.team && (
                        <span className="text-sm text-gray-500">{task.team.name}</span>
                      )}
                      {task.dueDate && (
                        <span className="text-sm text-gray-500">
                          {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setEditingTask(task);
                            setIsTaskModalOpen(true);
                          }}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title="Edit Task"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete Task"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedTask(task);
                            setIsTaskDetailsModalOpen(true);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="View Task Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMoveTaskToProject(task)}
                          className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                          title="Move to Project"
                        >
                          <FolderIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pagination Controls */}
              {tasksPaginationData && tasksPaginationData.totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <button
                    onClick={() => handleTasksPageChange(tasksCurrentPage - 1)}
                    disabled={tasksCurrentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(tasksPaginationData.totalPages, 5) }, (_, i) => {
                    let page: number;
                    if (tasksPaginationData.totalPages <= 5) {
                      page = i + 1;
                    } else if (tasksCurrentPage <= 3) {
                      page = i + 1;
                    } else if (tasksCurrentPage >= tasksPaginationData.totalPages - 2) {
                      page = tasksPaginationData.totalPages - 4 + i;
                    } else {
                      page = tasksCurrentPage - 2 + i;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handleTasksPageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          tasksCurrentPage === page
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handleTasksPageChange(tasksCurrentPage + 1)}
                    disabled={tasksCurrentPage === tasksPaginationData.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === 'recurring-tasks' && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Standalone Recurring Tasks</h3>
                <button
                  onClick={() => {
                    setEditingRecurringTask(null);
                    setIsRecurringTaskModalOpen(true);
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Recurring Task
                </button>
              </div>

              {/* User Filter */}
              <div className="mb-4">
                <select
                  value={recurringTasksUserFilter}
                  onChange={(e) => handleRecurringTasksUserFilterChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Users</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {recurringTasksPaginationData && (
                  <span className="ml-4 text-sm text-gray-500">
                    Showing {((recurringTasksPaginationData.page - 1) * recurringTasksPaginationData.limit) + 1} to {Math.min(recurringTasksPaginationData.page * recurringTasksPaginationData.limit, recurringTasksPaginationData.total)} of {recurringTasksPaginationData.total} recurring tasks
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {standaloneRecurringTasks.map((template, index) => (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <ArrowPathIcon className="w-5 h-5 text-blue-500" />
                          <h4 className="text-lg font-medium text-gray-900">{template.title}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            template.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {template.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {template.priority}
                          </span>
                        </div>

                        {template.description && (
                          <p className="text-gray-600 mb-2 line-clamp-2">{template.description}</p>
                        )}

                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                          <span>Every {template.recurrenceValue} {template.recurrenceType.toLowerCase()}</span>
                          {template.endDate && (
                            <span>Until {new Date(template.endDate).toLocaleDateString()}</span>
                          )}
                          {template.maxOccurrences && (
                            <span>Max {template.maxOccurrences} occurrences</span>
                          )}
                        </div>

                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          {template.team && (
                            <div className="flex items-center space-x-1">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: template.team.color }}
                              />
                              <span>{template.team.name}</span>
                            </div>
                          )}
                          <span>Created by {template.creator.firstName} {template.creator.lastName}</span>
                          {template.assignees && template.assignees.length > 0 && (
                            <span>Assigned to {template.assignees.length} user(s)</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleViewRecurringTaskDetails(template)}
                          className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditRecurringTask(template)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit Recurring Task"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRecurringTask(template)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete Recurring Task"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {standaloneRecurringTasks.length === 0 && (
                  <div className="text-center py-12">
                    <ArrowPathIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Recurring Tasks</h3>
                    <p className="text-gray-600">
                      Create a recurring task to get started.
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {recurringTasksPaginationData && recurringTasksPaginationData.totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                  <button
                    onClick={() => handleRecurringTasksPageChange(recurringTasksCurrentPage - 1)}
                    disabled={recurringTasksCurrentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(recurringTasksPaginationData.totalPages, 5) }, (_, i) => {
                    let page: number;
                    if (recurringTasksPaginationData.totalPages <= 5) {
                      page = i + 1;
                    } else if (recurringTasksCurrentPage <= 3) {
                      page = i + 1;
                    } else if (recurringTasksCurrentPage >= recurringTasksPaginationData.totalPages - 2) {
                      page = recurringTasksPaginationData.totalPages - 4 + i;
                    } else {
                      page = recurringTasksCurrentPage - 2 + i;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handleRecurringTasksPageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          recurringTasksCurrentPage === page
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handleRecurringTasksPageChange(recurringTasksCurrentPage + 1)}
                    disabled={recurringTasksCurrentPage === recurringTasksPaginationData.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === 'analytics' && (
          <div className="space-y-6">
            {!analytics ? (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <ChartBarIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-gray-500">Loading analytics...</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <FolderIcon className="w-8 h-8 text-blue-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-blue-600">Total Projects</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {analytics.stats?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <ClockIcon className="w-8 h-8 text-green-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-600">Total Tasks</p>
                        <p className="text-2xl font-bold text-green-900">
                          {analytics.stats?.reduce((sum: number, project: any) => sum + (project._count?.tasks || 0), 0) || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-purple-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <UserGroupIcon className="w-8 h-8 text-purple-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-purple-600">Active Teams</p>
                        <p className="text-2xl font-bold text-purple-900">
                          {analytics.stats?.reduce((teams: Set<string>, project: any) => {
                            project.teams?.forEach((pt: any) => teams.add(pt.team.name));
                            return teams;
                          }, new Set()).size || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 p-6 rounded-lg">
                    <div className="flex items-center">
                      <ChartBarIcon className="w-8 h-8 text-yellow-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-yellow-600">Completion Rate</p>
                        <p className="text-2xl font-bold text-yellow-900">
                          {analytics.stats ?
                            ((analytics.stats.filter((p: any) => p.status === 'COMPLETED').length / analytics.stats.length) * 100).toFixed(1)
                            : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Status Distribution */}
                {analytics.taskDistribution && analytics.taskDistribution.length > 0 && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Distribution</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {analytics.taskDistribution.map((item: any) => (
                        <div key={item.status} className="text-center">
                          <div className="text-2xl font-bold text-gray-900">{item.count}</div>
                          <div className={`text-sm px-2 py-1 rounded-full inline-block mt-1 ${getStatusColor(item.status)}`}>
                            {item.status.replace('_', ' ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Timeline */}
                {analytics.timeline && analytics.timeline.length > 0 && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Project Timeline</h3>
                    <div className="space-y-3">
                      {analytics.timeline.slice(0, 10).map((project: any) => (
                        <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: project.color || '#6B7280' }}
                            />
                            <div>
                              <p className="font-medium text-gray-900">{project.name}</p>
                              <p className="text-sm text-gray-500">
                                {project.teams?.join(', ') || 'No teams assigned'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                              {project.status}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {project._count?.tasks || 0} tasks
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Team Contributions */}
                {analytics.teamContribution && analytics.teamContribution.length > 0 && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Team Contributions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {analytics.teamContribution.map((team: any) => (
                        <div key={team.teamId} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: team.teamColor || '#6B7280' }}
                            />
                            <div>
                              <p className="font-medium text-gray-900">{team.teamName}</p>
                              <p className="text-sm text-gray-500">{team.taskCount} tasks</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={confirmDeleteProject}
          title="Delete Project"
          message={`Are you sure you want to delete "${projectToDelete?.name}"? This action cannot be undone and will also delete all associated tasks.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />

        {/* Project Modal */}
        <ProjectModal
          isOpen={isProjectModalOpen}
          onClose={() => {
            setIsProjectModalOpen(false);
            setEditingProject(null);
          }}
          onSave={handleSaveProject}
          project={editingProject}
          teams={teams}
        />

        {/* Task Modal */}
        <TaskModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setEditingTask(null);
          }}
          onSubmit={handleSaveTask}
          task={editingTask}
          teams={teams}
          users={users}
          projects={projects}
          creatorId={currentUser?.id}
          isLoading={false}
        />

        {/* Task Details Modal */}
        <TaskDetailsModal
          isOpen={isTaskDetailsModalOpen}
          onClose={() => {
            setIsTaskDetailsModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdated={() => {
            // Optionally reload data or update task state
            loadData();
          }}
        />

        {/* Recurring Task Modal */}
        <RecurringTaskModal
          isOpen={isRecurringTaskModalOpen}
          onClose={() => {
            setIsRecurringTaskModalOpen(false);
            setEditingRecurringTask(null);
          }}
          recurringTask={editingRecurringTask}
          onSave={() => {
            loadStandaloneRecurringTasks(); // Reload recurring tasks data after saving
          }}
          teams={teams}
          users={users}
          projects={projects}
        />

        {/* Recurring Task Details Modal */}
        <RecurringTaskDetailsModal
          isOpen={isRecurringTaskDetailsModalOpen}
          onClose={() => {
            setIsRecurringTaskDetailsModalOpen(false);
            setSelectedRecurringTask(null);
          }}
          recurringTask={selectedRecurringTask}
          onEdit={() => {
            setIsRecurringTaskDetailsModalOpen(false);
            handleEditRecurringTask(selectedRecurringTask);
          }}
          onDelete={() => {
            setIsRecurringTaskDetailsModalOpen(false);
            handleDeleteRecurringTask(selectedRecurringTask);
          }}
          currentUser={currentUser}
        />
      </div>
    </Layout>
  );
};

export default Projects;
